/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Paper from "@material-ui/core/Paper";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Card,
  // CardFooter,
  // CardHeader,
  Col,
  Container,
  Row,
  // Spinner,
  Table,
  Button,
  Spinner,
} from "reactstrap";
import EventsListScreenHeader from "../components/Headers/EventsListScreenHeader";
import { useAllEarningsCheckTicker } from "../hooks/useAllEarningsCheckTicker";
import { useQueryParams } from "../hooks/useQueryParams";
import { updateQueryParams } from "../utils/updateQueryParams";
import qs from "query-string";
import axiosClient from "../utils/axiosClient";

// const RESULTS_PER_PAGE = 10;

const HistoricalAnalysis: React.FC = () => {
  const query = useQueryParams();
  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get("page") || 0);
    if (isNaN(page) || page <= 0) {
      return 1;
    }
    return page;
  });
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [checkTickerError, setcheckTickerError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState(() => {
    const searchTerm = query.get("searchTerm");
    if (searchTerm) {
      // console.log("searchTerm----", searchTerm);
      return searchTerm;
    }
    return "";
  });
  const queryClient = useQueryClient();
  const location = useLocation();
  const router = useHistory();

  const { isError, isLoading, data, refetch } = useAllEarningsCheckTicker(
    pageTable,
    searchTerm,
    shouldFetchData
  );


  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addedItems, setAddedItems] = useState<any[]>([]);
  const [selectedItemsIds, setSelectedItemsIds] = useState<any[]>([]);

  useEffect(() => {
    handleConfSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTable]);

  const handleConfSearch = () => {
    if (isLoading) {
      return;
    }
    queryClient.removeQueries(["ticker", "status", "searchTerm"]);
    refetch();
  };

  useEffect(() => {
    if (shouldFetchData) {
      handleConfSearch();
      setShouldFetchData(false); // Reset the flag after search is performed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchData]);

  const handlePressEnter = () => {
    setPageTable(1);

    // handleConfSearch()
    // Convert search term to uppercase
    const upperCaseSearchTerm = searchTerm.toUpperCase();
    setSearchTerm(upperCaseSearchTerm);

    setShouldFetchData(true); 
    const q = qs.parse(location.search)
    q.searchTerm = upperCaseSearchTerm
    if (upperCaseSearchTerm.trim().length === 0) {
      delete q.searchTerm
    }
    // q.page = `${pageTable}`
    updateQueryParams(qs.stringify(q), router, location.pathname);
    if (data?.data === null || data?.data === undefined || data?.data.length === 0) {
      setcheckTickerError("No such company ticker exists");
    } else {
      setcheckTickerError(null); // Clear the error message if the search condition is false
    }
  };

  const handleAdd = async () => {
    setIsUploading(true);
    if (selectedYear && data?.data) {
      const selectedItems = groupedData[selectedYear].filter((item: any) =>
        selectedIds.includes(item.id)
      );
      // Add all selected items' IDs to selectedIds
      const newSelectedIds = selectedItems.map((item: any) => item.id);
      const newSelectedIdsTitle = selectedItems.map((item: any) => item.title);
      const newSelectedId = newSelectedIds[0];
      try {
        const blobUploadResponse = await axiosClient.post<any>(
          `/earningsDocuments/blob/upload`,
          { selectedId: newSelectedId }
        );
        const blobUrl = blobUploadResponse.data.blobUrl;

        setSelectedItemsIds((prevSelectedIds) => {
          const updatedSelectedIds = [
            ...prevSelectedIds,
            ...newSelectedIds
              .filter(
                (id: any) => !prevSelectedIds.some((item) => item.id === id)
              )
              .map((id: any, index: number) => ({ id, blobUrl, title:  newSelectedIdsTitle[index]})),
          ];
          return updatedSelectedIds;
        });

        setAddedItems([...addedItems, ...selectedItems]);
        setIsUploading(false);
        setSubmissionError(null);
      } catch (error) {
        setIsUploading(false);
        console.error("Error uploading document to blob:", error);
        throw error;
      }
    }
  };

  const handleAnalyze = async () => {

    try {
      if (
        selectedItemsIds &&
        selectedItemsIds.length >= 2 &&
        selectedItemsIds.every((id) => id.blobUrl)
      ) {
        setIsSubmitted(true);
        let executionId = "";

        try {
          const response = await axiosClient.post(
            "earningsExecution/create",
            // requestBody,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const executionResponse = response.data;
          executionId = executionResponse.id;
          // console.log("Execution Response:", executionId);
        } catch (error) {
          setIsSubmitted(false);
          setSubmissionError("Error creating execution");
          console.error("Error creating execution:", error);
          throw error;
        }

        // Add your analyze logic here
        // post request to /earningsCall/analyze
        // const selectedItemsIdsDummy = [
        //   {
        //     id: 'AAPL_3371668',
        //     blobUrl: 'https://ftseautomationsg.blob.core.windows.net/dev-assets/combined_transcript_1718013181569.pdf'
        //   },
        //   {
        //     id: 'AAPL_3382255',
        //     blobUrl: 'https://ftseautomationsg.blob.core.windows.net/dev-assets/combined_transcript_1718013345000.pdf'
        //   }
        // ];

        const promises = selectedItemsIds.map(async (item) => {
          try {
            // Remove "Earnings call" from the title if it exists
            const cleanedTitle = item.title.replace(/Earnings call/gi, '').trim();

            await axiosClient.post(
              "/earningsIdentifier/identifier/upload",
              {
                earningsExecutionId: executionId,
                blob_url: item.blobUrl,
                identifierName: cleanedTitle,
              }
            );
            
          } catch (error) {
            setSubmissionError("Error in uploading files.");
            console.error("Error uploading file:", error);            
          }
        });

        try {
          // Wait for all promises to resolve (execute in parallel)
          await Promise.all(promises);
        } catch (error) {
          // Handle any errors that occurred during parallel execution
          console.error("Error in parallel execution:", error);
        }

        router.push(
          `/admin/earningsCall/conversation?conversationId=${executionId}`
        );

        setIsSubmitted(false);

        setSelectedIds([]);
      } else {
        setSubmissionError("Please select at least two document to analyze");
        return;
      }
    } catch (error) {
      setSubmissionError("Error in handling file submission");
      console.error("Error handling file submission: ", error);
      setIsSubmitted(false); // Reset isSubmitted in case of an error
    }
  };

  const [selectedYear, setSelectedYear] = useState("");

  const groupedData = data?.data.reduce((acc: any, item: any) => {
    if (!acc[item.year]) {
      acc[item.year] = [];
    }
    acc[item.year].push(item);
    return acc;
  }, {});

  let sortedYears: string[] = [];

  if (groupedData) {
    sortedYears = Object.keys(groupedData).sort(
      (a, b) => Number(b) - Number(a)
    );
  }

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = event.target.value;
    setSelectedYear(year);
  };

  let content: JSX.Element | null = null;

  if (isError) {
    content = <Alert color="danger">Something went wrong!</Alert>;
  }
  // else if (isLoading) {
  //     content = <Spinner />;
  // }
  else {
    content = (
      <div>
        <Container>
          <Row className="d-flex justify-content-center">
            <div className="p-4 text-center text-xl font-bold">
              Start your conversation by selecting the documents you want to
              explore
            </div>
          </Row>

          <Row className="d-flex justify-content-center">
            <Col>
              <div className="input-group-alternative input-group">
                <div className="input-group-prepend">
                  <span onClick={handlePressEnter} className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                </div>
                <input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "enter") {
                      handlePressEnter();
                    }
                  }}
                  placeholder="Search for any company ticker"
                  type="text"
                  className="form-control"
                />
                {searchTerm && checkTickerError && (data?.data === null || data?.data === undefined || data?.data.length === 0) &&(<div className="form-display-error">
                    <span style={{ color: "red" }}>{checkTickerError}</span>
                  </div>)}
              </div>
            </Col>
            <Col>
              <div className="input-group-alternative input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <i className="fas fa-calendar"></i>
                  </span>
                </div>
                <select className="form-control" onChange={handleYearChange}>
                  <option value="">Select Year</option>
                  {data?.data &&
                  data?.data !== null &&
                  data?.data !== undefined &&
                  data?.data.length !== 0 &&                   
                    sortedYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                </select>
              </div>
            </Col>
            <Col>
              <div className="input-group-alternative input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <i className="fas fa-calendar-alt"></i>
                  </span>
                </div>
                <select
                  className="form-control"
                  disabled={!selectedYear}
                  // multiple
                  value={selectedIds}
                  onChange={(e) =>
                    setSelectedIds(
                      Array.from(
                        e.target.selectedOptions,
                        (option) => option.value
                      )
                    )
                  }
                >
                  <option value="">Select Quarter</option>
                  {data?.data &&
                  data?.data !== null &&
                    data?.data !== undefined &&    
                    data?.data.length !== 0 &&                
                    selectedYear &&
                    groupedData[selectedYear].map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {`Q${item.quarter}`}
                      </option>
                    ))}
                </select>
              </div>
            </Col>
            <Col>
              <Button
                onClick={handleAdd}
                style={{ backgroundColor: "lightblue", color: "black" }}
                disabled={!selectedItemsIds || isUploading || isSubmitted || !searchTerm || !selectedYear || !selectedIds || selectedIds.length === 0}
              >
                {isUploading ? (
                  <>
                    <Spinner style={{ width: '1.5rem', height: '1.5rem' }}/>
                    <span >Adding...</span>
                  </>
                ) : (
                  "Add"
                )}

                
              </Button>{" "}
              {!selectedItemsIds && isSubmitted && (
                  <div>
                    <span style={{ color: "red" }}>Please select at least one file!</span>
                  </div>
                )}
            </Col>
          </Row>
          <br />

          <Row>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Title</th>
                  <th>Year</th>
                  <th>Quarter</th>
                </tr>
              </thead>
              <tbody>
                {addedItems && addedItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.symbol}</td>
                    <td>{item.title}</td>
                    <td>{item.year}</td>
                    <td>{item.quarter}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Row>

          <br />
          <Row className="d-flex justify-content-center">
            <Button
              onClick={handleAnalyze}
              style={{ backgroundColor: "lightblue", color: "black" }}
              disabled={isSubmitted || isUploading}
            >
              {isSubmitted ? (
                <>
                <Spinner style={{ width: '1.5rem', height: '1.5rem' }} />
                <span className="ml-2">Analyzing...</span>
                </>
              ) : "Analyze"
              }
            </Button>

            

          </Row>
          <Row className="d-flex justify-content-center">
          {submissionError && (
              <div className="form-display-error">
                <span style={{ color: "red" }}>{submissionError}</span>
              </div>
            )}
          </Row>
        </Container>
        <br />
      </div>
    );
  }
  return (
    <>
      <EventsListScreenHeader />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <Paper className="px-4">
                <div className="mb-3">
                  {/* <h4 style={{ display: "inline" }}></h4> */}
                </div>
                {content}
              </Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default HistoricalAnalysis;
